'use client';

import { createClient } from '@/lib/supabase/client';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  featuredImageUrl?: string;
  status: 'published' | 'draft';
  displayOrder: number;
  tags?: string[];
  keywords?: string;
  keywordsData?: import('@/lib/services/keywordService').Keyword[];
  internalLinks?: InternalLink[];
  scheduledAt?: string;
  publishedAt?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

interface InternalLink {
  url: string;
  title: string;
}

interface BlogPostInput {
  title: string;
  slug?: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  featuredImageUrl?: string;
  status: 'published' | 'draft';
  displayOrder?: number;
  tags?: string[];
  keywords?: string;
  keywordsData?: import('@/lib/services/keywordService').Keyword[];
  internalLinks?: InternalLink[];
  scheduledAt?: string;
  publishedAt?: string;
}

export interface BlogPostsByCategory {
  category: string;
  posts: BlogPost[];
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const blogService = {
  async getAll(): Promise<BlogPost[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug || '',
        excerpt: row.excerpt,
        content: row.content,
        author: row.author,
        category: row.category,
        featuredImageUrl: row.featured_image_url,
        status: (row.status as 'published' | 'draft') || 'draft',
        displayOrder: row.display_order || 0,
        tags: row.tags || [],
        keywords: row.keywords || '',
        keywordsData: Array.isArray(row.keywords_data) ? row.keywords_data : [],
        internalLinks: row.internal_links || [],
        scheduledAt: row.scheduled_at,
        publishedAt: row.published_at,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get blog posts error:', error.message);
      throw error;
    }
  },

  async getPublished(): Promise<BlogPost[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .or('published_at.is.null,published_at.lte.' + new Date().toISOString())
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug || '',
        excerpt: row.excerpt,
        content: row.content,
        author: row.author,
        category: row.category,
        featuredImageUrl: row.featured_image_url,
        featuredImageAlt: row.featured_image_alt,
        status: row.status,
        displayOrder: row.display_order || 0,
        tags: row.tags || [],
        keywords: row.keywords || '',
        keywordsData: Array.isArray(row.keywords_data) ? row.keywords_data : [],
        internalLinks: row.internal_links || [],
        scheduledAt: row.scheduled_at,
        publishedAt: row.published_at,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get published blog posts error:', error.message);
      throw error;
    }
  },

  async getByCategory(category: string): Promise<BlogPost[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('category', category)
        .eq('status', 'published')
        .or('published_at.is.null,published_at.lte.' + new Date().toISOString())
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug || '',
        excerpt: row.excerpt,
        content: row.content,
        author: row.author,
        category: row.category,
        featuredImageUrl: row.featured_image_url,
        status: row.status,
        displayOrder: row.display_order || 0,
        tags: row.tags || [],
        keywords: row.keywords || '',
        keywordsData: Array.isArray(row.keywords_data) ? row.keywords_data : [],
        internalLinks: row.internal_links || [],
        scheduledAt: row.scheduled_at,
        publishedAt: row.published_at,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get blog posts by category error:', error.message);
      throw error;
    }
  },

  async getPublishedGroupedByCategory(): Promise<BlogPostsByCategory[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .or('published_at.is.null,published_at.lte.' + new Date().toISOString())
        .order('category', { ascending: true })
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      const posts = (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug || '',
        excerpt: row.excerpt,
        content: row.content,
        author: row.author,
        category: row.category,
        featuredImageUrl: row.featured_image_url,
        status: row.status,
        displayOrder: row.display_order || 0,
        tags: row.tags || [],
        keywords: row.keywords || '',
        keywordsData: Array.isArray(row.keywords_data) ? row.keywords_data : [],
        internalLinks: row.internal_links || [],
        scheduledAt: row.scheduled_at,
        publishedAt: row.published_at,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));

      // Group posts by category
      const grouped = posts.reduce((acc, post) => {
        const existing = acc.find((g) => g.category === post.category);
        if (existing) {
          existing.posts.push(post);
        } else {
          acc.push({ category: post.category, posts: [post] });
        }
        return acc;
      }, [] as BlogPostsByCategory[]);

      return grouped;
    } catch (error: any) {
      console.error('Get grouped blog posts error:', error.message);
      throw error;
    }
  },

  async getAllCategories(): Promise<string[]> {
    const supabase = createClient();

    try {
      // Try to get categories from blog_categories table first
      const { data: categoryData, error: categoryError } = await supabase
        .from('blog_categories')
        .select('name')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (!categoryError && categoryData && categoryData.length > 0) {
        return categoryData.map((row) => row.name);
      }

      // Fallback to extracting from blog_posts if blog_categories table doesn't exist or is empty
      const { data, error } = await supabase
        .from('blog_posts').select('category').order('category', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      const uniqueCategories = Array.from(
        new Set((data || []).map((row) => row.category).filter(Boolean))
      );
      return uniqueCategories;
    } catch (error: any) {
      console.error('Get all categories error:', error.message);
      throw error;
    }
  },

  async getAllTags(): Promise<string[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('blog_posts').select('tags').eq('status', 'published');

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      // Flatten and get unique tags
      const allTags = (data || []).flatMap((item) => item.tags || []);
      const uniqueTags = [...new Set(allTags)];
      return uniqueTags.sort();
    } catch (error: any) {
      console.error('Get blog tags error:', error.message);
      throw error;
    }
  },

  async getByTag(tag: string): Promise<BlogPost[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('blog_posts').select('*').contains('tags', [tag])
        .eq('status', 'published').order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        title: row.title,
        slug: row.slug || '',
        excerpt: row.excerpt,
        content: row.content,
        author: row.author,
        category: row.category,
        featuredImageUrl: row.featured_image_url,
        status: row.status,
        displayOrder: row.display_order || 0,
        tags: row.tags || [],
        keywords: row.keywords || '',
        keywordsData: Array.isArray(row.keywords_data) ? row.keywords_data : [],
        internalLinks: row.internal_links || [],
        scheduledAt: row.scheduled_at,
        publishedAt: row.published_at,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get blog posts by tag error:', error.message);
      throw error;
    }
  },

  async create(post: Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'>): Promise<BlogPost | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .insert({
          title: post.title,
          slug: post.slug || '',
          excerpt: post.excerpt,
          content: post.content,
          author: post.author,
          category: post.category,
          featured_image_url: post.featuredImageUrl,
          status: post.status,
          display_order: post.displayOrder || 0,
          tags: post.tags || [],
          keywords: post.keywords || '',
          keywords_data: post.keywordsData || [],
          internal_links: post.internalLinks || [],
          scheduled_at: post.scheduledAt || null,
          published_at: post.publishedAt || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        title: data.title,
        slug: data.slug || '',
        excerpt: data.excerpt,
        content: data.content,
        author: data.author,
        category: data.category,
        featuredImageUrl: data.featured_image_url,
        status: data.status,
        displayOrder: data.display_order || 0,
        tags: data.tags || [],
        keywords: data.keywords || '',
        keywordsData: data.keywords_data || [],
        internalLinks: data.internal_links || [],
        scheduledAt: data.scheduled_at,
        publishedAt: data.published_at,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Create blog post error:', error.message);
      throw error;
    }
  },

  async update(id: string, post: Partial<Omit<BlogPost, 'id' | 'createdAt' | 'updatedAt'>>): Promise<BlogPost | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (post.title !== undefined) updateData.title = post.title;
      if (post.slug !== undefined) updateData.slug = post.slug;
      if (post.excerpt !== undefined) updateData.excerpt = post.excerpt;
      if (post.content !== undefined) updateData.content = post.content;
      if (post.author !== undefined) updateData.author = post.author;
      if (post.category !== undefined) updateData.category = post.category;
      if (post.featuredImageUrl !== undefined) updateData.featured_image_url = post.featuredImageUrl;
      if (post.status !== undefined) updateData.status = post.status;
      if (post.displayOrder !== undefined) updateData.display_order = post.displayOrder;
      if (post.tags !== undefined) updateData.tags = post.tags;
      if (post.keywords !== undefined) updateData.keywords = post.keywords;
      if (post.keywordsData !== undefined) updateData.keywords_data = post.keywordsData;
      if (post.internalLinks !== undefined) updateData.internal_links = post.internalLinks;
      if (post.scheduledAt !== undefined) updateData.scheduled_at = post.scheduledAt;
      if (post.publishedAt !== undefined) updateData.published_at = post.publishedAt;

      const { data, error } = await supabase
        .from('blog_posts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        title: data.title,
        slug: data.slug || '',
        excerpt: data.excerpt,
        content: data.content,
        author: data.author,
        category: data.category,
        featuredImageUrl: data.featured_image_url,
        status: data.status,
        displayOrder: data.display_order || 0,
        tags: data.tags || [],
        keywords: data.keywords || '',
        keywordsData: data.keywords_data || [],
        internalLinks: data.internal_links || [],
        scheduledAt: data.scheduled_at,
        publishedAt: data.published_at,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Update blog post error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return false;
        }
      }

      return true;
    } catch (error: any) {
      console.error('Delete blog post error:', error.message);
      throw error;
    }
  },

  async getRelatedPosts(currentPostId: string, limit: number = 3): Promise<BlogPost[]> {
    const supabase = createClient();

    try {
      // Get current post
      const { data: currentPostData, error: currentError } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('id', currentPostId)
        .single();

      if (currentError || !currentPostData) {
        console.error('Error fetching current post:', currentError);
        return [];
      }

      const currentPost = {
        id: currentPostData.id,
        category: currentPostData.category,
        tags: currentPostData.tags || [],
        keywords: currentPostData.keywords || '',
      };

      // Get all published posts except current
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('status', 'published')
        .neq('id', currentPostId);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      if (!data || data.length === 0) return [];

      // Calculate relevance score for each post
      const scoredPosts = data.map((row) => {
        let score = 0;
        const post = {
          id: row.id,
          title: row.title,
          slug: row.slug || '',
          excerpt: row.excerpt,
          content: row.content,
          author: row.author,
          category: row.category,
          featuredImageUrl: row.featured_image_url,
          status: row.status,
          displayOrder: row.display_order || 0,
          tags: row.tags || [],
          keywords: row.keywords || '',
          internalLinks: row.internal_links || [],
          createdBy: row.created_by,
          scheduledAt: row.scheduled_at,
          publishedAt: row.published_at,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        };

        // Category match: +10 points
        if (post.category === currentPost.category) {
          score += 10;
        }

        // Tag matches: +5 points per shared tag
        const sharedTags = post.tags.filter((tag: string) => 
          currentPost.tags.includes(tag)
        );
        score += sharedTags.length * 5;

        // Keyword matches: +3 points per shared keyword
        if (currentPost.keywords && post.keywords) {
          const currentKeywords = currentPost.keywords
            .toLowerCase()
            .split(/[,;\s]+/)
            .filter((k: string) => k.length > 0);
          const postKeywords = post.keywords
            .toLowerCase()
            .split(/[,;\s]+/)
            .filter((k: string) => k.length > 0);
          
          const sharedKeywords = postKeywords.filter((keyword: string) => 
            currentKeywords.includes(keyword)
          );
          score += sharedKeywords.length * 3;
        }

        return { post, score };
      });

      // Sort by score (descending) and return top posts
      const relatedPosts = scoredPosts
        .filter(({ score }) => score > 0) // Only include posts with some relevance
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map(({ post }) => post);

      return relatedPosts;
    } catch (error: any) {
      console.error('Get related posts error:', error.message);
      throw error;
    }
  },

  async checkSlugUnique(slug: string, excludeId?: string): Promise<boolean> {
    const supabase = createClient();
    try {
      let query = supabase
        .from('blog_posts')
        .select('id')
        .eq('slug', slug);

      if (excludeId) {
        query = query.neq('id', excludeId);
      }

      const { data, error } = await query;
      if (error) return true; // assume unique on error
      return !data || data.length === 0;
    } catch {
      return true;
    }
  },
};